import type { Axios } from "axios";

import { CLAIMS_SORT_FIELD_MAP } from "../../api/filters/claimFilters";
import type {
  BackendFilterableSearchRequest,
  BackendFilters,
} from "../../utils/backendFilters";
import {
  getClaimsProceduresAndPatient,
  postUpdate,
} from "../ClaimsDashboard/api";

export interface PatientsSearchParams {
  filters: BackendFilters;
  page: number;
  pageSize: number;
  sortField: string | null;
  sortDirection: "asc" | "desc";
}

export interface PatientProfile {
  patient: PatientWithClaimMessage | undefined;
  claims: ClaimWithProcedureAndPatientMessage[];
}

export const getPatients = async (
  c: Axios,
  params: PatientsSearchParams,
  signal?: AbortSignal,
) => {
  const { filters: backendFilters } = params;

  const requestBody: BackendFilterableSearchRequest = {
    page: params.page,
    page_size: params.pageSize,
    sort: params.sortField ? [params.sortField] : [],
    sort_direction: params.sortDirection,
    filters: { ...backendFilters.filters },
  };

  if (backendFilters.datagridFilters?.length) {
    requestBody.datagrid_filters = backendFilters.datagridFilters;
  }
  if (backendFilters.logicalOperator) {
    requestBody.logical_operator = backendFilters.logicalOperator;
  }

  const response = await c.post<SearchResponse>(
    "/patients/search",
    requestBody,
    { signal },
  );
  return response.data;
};

export const getPatientWithClaims = async (
  c: Axios,
  patientId: string,
  payerId: string | null,
  devMode: boolean,
): Promise<PatientProfile> => {
  const response = await c.get<PatientWithClaimMessage>(
    `/patients/${patientId}`,
  );
  const patient = response.data;
  const claimIds = (patient?.Claim ?? []).map((claim) => claim.wieldyId);
  const claims = claimIds.length
    ? (
        await getClaimsProceduresAndPatient(c, {
          filters: {
            filters: {
              ...(payerId ? { payer_id: [payerId] } : {}),
              wieldy_id: claimIds,
            },
          },
          tab: "",
          page: 1,
          pageSize: 500,
          sortField: CLAIMS_SORT_FIELD_MAP.availableSince,
          sortDirection: "desc",
          view: null,
          deniseQueue: null,
          devMode,
        })
      ).data
    : [];
  return { patient, claims };
};

export const postPatient = (c: Axios, patient: CreatePatientMessage) =>
  postUpdate(c, { type: "Patient", payload: patient });

export const updatePatient = (c: Axios, patient: PatientMessage) =>
  postUpdate(c, { type: "Patient", payload: patient }, { sync: true });

export const postPatientPayment = async (
  c: Axios,
  patientId: string,
  token: string,
) => {
  const response = await c.post<PatientPaymentMessage>(
    `patient/${patientId}/payment-methods`,
    { token },
  );
  return response.data;
};

export const getPatientInsurance = async (c: Axios, patientId: string) => {
  const response = await c.get<PatientInsuranceMessage[]>(
    `/patients/${patientId}/insurance`,
  );
  return response.data;
};

export const getPatientInsuranceVerifications = async (
  c: Axios,
  patientId: string,
  insuranceKey: string,
) => {
  const response = await c.get<PatientInsuranceVerificationSummary[]>(
    `/patients/${patientId}/insurance/${insuranceKey}/verifications`,
  );
  return response.data;
};
